import React from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Grid,
  Button,
  Typography,
  Paper,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
  Switch,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { AddCircleOutlineOutlined, EditOutlined, SyncOutlined } from "@mui/icons-material";
import { postRequest, getRequest, patchRequest, deleteRequest } from "Includes/api/apicall";
import { DEL_URL, GET_URL, POST_URL } from "Includes/urls";
import { TEAM_STATUS_CHOICES } from "Includes/teamStatus";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import ReactPhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

function CompanyList(props) {
  const navigate = useNavigate();
  const location = useLocation();
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);
  const [nameInput, setNameInput] = React.useState("");
  const [nameSearch, setNameSearch] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("active");
  const [statusByTeamFilter, setStatusByTeamFilter] = React.useState("all");
  const [featuresFilter, setFeaturesFilter] = React.useState(["payment_gateway", "website", "biometric"]);
  const [coordinatorFilter, setCoordinatorFilter] = React.useState("all");
  const [coordinatorDialogOpen, setCoordinatorDialogOpen] = React.useState(false);
  const [coordinatorRow, setCoordinatorRow] = React.useState(null);
  const [coordinatorUsers, setCoordinatorUsers] = React.useState([]);
  const [summaryData, setSummaryData] = React.useState({ overall: { open: 0, closed: 0, total: 0 }, company_wise: [] });
  const [activeTab, setActiveTab] = React.useState(0);
  const [selectedSummaryCompany, setSelectedSummaryCompany] = React.useState(null);

  const getTicketSummary = () => {
    getRequest(GET_URL.ticket.api + 'summary/', {}, props)
      .then(response => {
        if (response && response.status === 200) {
          setSummaryData(response.data);
        }
      })
      .catch(error => {
        console.error('Error fetching ticket summary:', error);
      });
  };
  const [selectedCoordinator, setSelectedCoordinator] = React.useState(null);
  const [savingCoordinator, setSavingCoordinator] = React.useState(false);
  const [ownerDialogOpen, setOwnerDialogOpen] = React.useState(false);
  const [ownerRows, setOwnerRows] = React.useState([]);
  const [ownerRowCount, setOwnerRowCount] = React.useState(0);
  const [ownerPage, setOwnerPage] = React.useState(0);
  const [ownerPageSize, setOwnerPageSize] = React.useState(10);
  const [ownerLoading, setOwnerLoading] = React.useState(false);
  const [ownerSearchInput, setOwnerSearchInput] = React.useState("");
  const [ownerSearch, setOwnerSearch] = React.useState("");
  const [ownerNameNotFilled, setOwnerNameNotFilled] = React.useState(false);
  const [ownerEmailNotFilled, setOwnerEmailNotFilled] = React.useState(false);
  const [ownerNumberNotFilled, setOwnerNumberNotFilled] = React.useState(false);
  const [savingOwnerId, setSavingOwnerId] = React.useState(null);
  const ownerRequestRef = React.useRef(0);
  const [sortModel, setSortModel] = React.useState([]);
  const latestRequestRef = React.useRef(0);

  const formatCreatedAt = React.useCallback((value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, []);
  const userInfo = React.useMemo(() => JSON.parse(localStorage.getItem("user") || "{}"), []);
  const allowedCodes = React.useMemo(() => {
    if (Array.isArray(userInfo?.permission_codenames) && userInfo.permission_codenames.length) {
      return new Set(userInfo.permission_codenames.filter(Boolean));
    }
    const ownCodes = (userInfo?.user_permissions || userInfo?.permissions || []).map((p) =>
      typeof p === "string" ? p : p?.codename
    );
    const groupCodes = (userInfo?.groups || [])
      .flatMap((g) => g?.permissions || [])
      .map((p) => (typeof p === "string" ? p : p?.codename));
    return new Set([...ownCodes, ...groupCodes].filter(Boolean));
  }, [userInfo]);
  const isSuperuser = Boolean(userInfo?.is_superuser);
  const canCreate = isSuperuser || allowedCodes.has("visible_company_add");
  const canUpdate = isSuperuser || allowedCodes.has("visible_company_change");
  const canDelete = isSuperuser || allowedCodes.has("visible_company_delete");
  const statusOptions = React.useMemo(
    () => ["Active", "Low Engagement", "At Risk", "Dead"],
    []
  );
  const statusStyle = React.useCallback((val) => {
    const v = String(val || "Active");
    const styles = {
      Active: { bgcolor: "#ECFDF3", color: "#027A48", borderColor: "#A6F4C5" },
      "Low Engagement": { bgcolor: "#EFF8FF", color: "#175CD3", borderColor: "#B2DDFF" },
      "At Risk": { bgcolor: "#FFFAEB", color: "#B54708", borderColor: "#FEDF89" },
      Dead: { bgcolor: "#FEF3F2", color: "#B42318", borderColor: "#FECDCA" },
    };
    return styles[v] || { bgcolor: "#F2F4F7", color: "#344054", borderColor: "#D0D5DD" };
  }, []);

  React.useEffect(() => {
    const t = setTimeout(() => {
      const trimmed = nameInput.trim();
      setNameSearch((prev) => {
        if (prev !== trimmed) {
          setPage(0);
        }
        return trimmed;
      });
    }, 400);
    return () => clearTimeout(t);
  }, [nameInput]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const st = (params.get("status") || "").toLowerCase();
    if (st === "active" || st === "inactive") {
      setStatusFilter(st);
      setPage(0);
    }
    const teamSt = (params.get("status_by_team") || "").toLowerCase();
    if (["all", "active", "low_engagement", "at_risk", "dead"].includes(teamSt)) {
      setStatusByTeamFilter(teamSt || "all");
      setPage(0);
    }
  }, [location.search]);

  React.useEffect(() => {
    getRequest(GET_URL.user.api, { groups: "4" }, props).then((res) => {
      if (res?.status === 200) {
        setCoordinatorUsers(res.data.data || res.data || []);
      }
    });
    getTicketSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    getCompanyList(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, nameSearch, statusFilter, statusByTeamFilter, featuresFilter, coordinatorFilter, sortModel]);

  const handleEdit = (id) => {
    navigate('/company/edit/', { state: { detail: id } });
  };

  const handleDetails = (id) => {
    navigate(`/company/details/${id}`);
  };

  const handleDelete = (id) => {
    deleteRequest(
      `${DEL_URL.company.api}${id}/`,
      {},
      { ...props, confirmButtonText: "Yes, delete company!" }
    ).then((response) => {
      if (response && response.status === 200) {
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Company deactivated",
          showConfirmButton: false,
          timer: 1400,
        });
        getCompanyList(page, pageSize);
      }
    });
  };

  const openOwnerDialog = () => {
    setOwnerSearchInput("");
    setOwnerSearch("");
    setOwnerNameNotFilled(false);
    setOwnerEmailNotFilled(false);
    setOwnerNumberNotFilled(false);
    setOwnerPage(0);
    setOwnerDialogOpen(true);
  };

  const handleOwnerDialogClose = () => {
    setOwnerDialogOpen(false);
    setOwnerRows([]);
    setOwnerRowCount(0);
    setOwnerSearchInput("");
    setOwnerSearch("");
    setOwnerNameNotFilled(false);
    setOwnerEmailNotFilled(false);
    setOwnerNumberNotFilled(false);
    setSavingOwnerId(null);
  };

  const getOwnerList = React.useCallback(
    (nextPage = ownerPage, nextPageSize = ownerPageSize, search = ownerSearch) => {
      const requestId = ownerRequestRef.current + 1;
      ownerRequestRef.current = requestId;
      setOwnerLoading(true);
      const params = {
        limit: nextPageSize,
        pageno: nextPage + 1,
        status: "active",
      };
      if (search) {
        params.name = search;
      }
      if (ownerNameNotFilled) {
        params.ownername_not_filled = true;
      }
      if (ownerEmailNotFilled) {
        params.owneremail_not_filled = true;
      }
      if (ownerNumberNotFilled) {
        params.phonenumber_not_filled = true;
      }
      getRequest(GET_URL.company.api, params, props)
        .then((response) => {
          if (ownerRequestRef.current !== requestId) return;
          if (response && response.status === 200) {
            const data = (response.data.data || []).map((row) => ({
              ...row,
              ownername: row.ownername || "",
              phonenumber: row.phonenumber || "91",
              owneremail: row.owneremail || "",
              send_notification: row.send_notification !== false,
            }));
            setOwnerRows(data);
            setOwnerRowCount(response.data?.count || data.length);
          }
        })
        .finally(() => {
          if (ownerRequestRef.current === requestId) {
            setOwnerLoading(false);
          }
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ownerPage, ownerPageSize, ownerSearch, ownerNameNotFilled, ownerEmailNotFilled, ownerNumberNotFilled]
  );

  React.useEffect(() => {
    if (!ownerDialogOpen) return;
    const t = setTimeout(() => {
      const trimmed = ownerSearchInput.trim();
      setOwnerSearch((prev) => {
        if (prev !== trimmed) {
          setOwnerPage(0);
        }
        return trimmed;
      });
    }, 400);
    return () => clearTimeout(t);
  }, [ownerSearchInput, ownerDialogOpen]);

  React.useEffect(() => {
    if (!ownerDialogOpen) return;
    getOwnerList(ownerPage, ownerPageSize, ownerSearch);
  }, [
    ownerDialogOpen,
    ownerPage,
    ownerPageSize,
    ownerSearch,
    ownerNameNotFilled,
    ownerEmailNotFilled,
    ownerNumberNotFilled,
    getOwnerList,
  ]);

  const handleOwnerFieldChange = (id, field, value) => {
    setOwnerRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  };

  const copyCompanyCode = async (row) => {
    const code = (row?.code || "").trim();
    if (!code) {
      Swal.fire({
        position: "top-end",
        icon: "info",
        title: "Company code not available",
        showConfirmButton: false,
        timer: 1200,
      });
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const el = document.createElement("textarea");
        el.value = code;
        el.setAttribute("readonly", "");
        el.style.position = "absolute";
        el.style.left = "-9999px";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      Swal.fire({
        position: "top-end",
        icon: "success",
        title: `Copied ${code}`,
        showConfirmButton: false,
        timer: 1000,
      });
    } catch {
      Swal.fire({ icon: "error", title: "Unable to copy company code" });
    }
  };

  const renderCompanyNameCell = (params) => (
    <Box
      title="Double-click to copy company code"
      sx={{
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        cursor: "pointer",
      }}
    >
      {params.row?.name || "-"}
    </Box>
  );

  const handleSaveOwnerRow = async (row) => {
    if (!row?.id) return;
    setSavingOwnerId(row.id);
    try {
      const res = await patchRequest(
        `${GET_URL.company.api}${row.id}/`,
        {
          ownername: row.ownername || "",
          phonenumber: row.phonenumber || "",
          owneremail: row.owneremail || "",
          send_notification: row.send_notification !== false,
        },
        props
      );
      if (res?.status === 200) {
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Owner details updated",
          showConfirmButton: false,
          timer: 1000,
        });
      }
    } catch {
      Swal.fire({ icon: "error", title: "Unable to update owner details" });
    } finally {
      setSavingOwnerId(null);
    }
  };

  const ownerColumns = React.useMemo(
    () => [
      {
        field: "code",
        headerName: "Code",
        flex: 0.8,
        minWidth: 110,
      },
      {
        field: "name",
        headerName: "Institute Name",
        flex: 1.2,
        minWidth: 180,
      },
      {
        field: "ownername",
        headerName: "Owner Name",
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => (
          <TextField
            size="small"
            fullWidth
            value={params.row.ownername || ""}
            onChange={(e) => handleOwnerFieldChange(params.row.id, "ownername", e.target.value)}
            placeholder="Enter owner name"
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      {
        field: "phonenumber",
        headerName: "Owner Contact",
        flex: 1.2,
        minWidth: 220,
        sortable: false,
        renderCell: (params) => (
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: "100%",
              "& .react-tel-input": { width: "100%" },
              "& .form-control": {
                width: "100% !important",
                height: "40px !important",
                fontSize: "14px",
              },
              "& .flag-dropdown": { zIndex: 5 },
            }}
          >
            <ReactPhoneInput
              country="in"
              value={params.row.phonenumber || "91"}
              onChange={(value) =>
                handleOwnerFieldChange(params.row.id, "phonenumber", value || "91")
              }
              inputProps={{ name: `owner-contact-${params.row.id}` }}
              specialLabel=""
            />
          </Box>
        ),
      },
      {
        field: "owneremail",
        headerName: "Owner Mail",
        flex: 1.2,
        minWidth: 180,
        sortable: false,
        renderCell: (params) => (
          <TextField
            size="small"
            fullWidth
            value={params.row.owneremail || ""}
            onChange={(e) => handleOwnerFieldChange(params.row.id, "owneremail", e.target.value)}
            placeholder="Enter owner mail"
            onClick={(e) => e.stopPropagation()}
          />
        ),
      },
      {
        field: "send_notification",
        headerName: "Send Notification",
        flex: 0.7,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => (
          <Switch
            checked={params.row.send_notification !== false}
            onChange={(e) => {
              e.stopPropagation();
              handleOwnerFieldChange(params.row.id, "send_notification", e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            color="success"
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        width: 110,
        renderCell: (params) => (
          <Button
            size="small"
            variant="contained"
            disabled={savingOwnerId === params.row.id}
            onClick={(e) => {
              e.stopPropagation();
              handleSaveOwnerRow(params.row);
            }}
            startIcon={savingOwnerId === params.row.id ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ textTransform: "none" }}
          >
            Save
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savingOwnerId]
  );

  const handleSyncOwnerDetails = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You want to sync owner details to the daily report config?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, sync it!',
    }).then((result) => {
      if (result.value) {
        setLoading(true);
        postRequest(POST_URL.servercommand.api, { command: "sync_owner_details" }, props)
          .then((res) => {
            if (res && res.status === 200) {
              Swal.fire({
                icon: 'success',
                title: 'Success',
                text: res.data?.Reason || 'Sync process initiated. You will receive an email shortly.',
              });
            }
          })
          .catch((err) => {
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: err?.response?.data?.Reason || 'Error initiating sync',
            });
          })
          .finally(() => setLoading(false));
      }
    });
  };

  const openAssignCoordinator = (row) => {
    setCoordinatorRow(row);
    const existing = coordinatorUsers.find((u) => u.id === row?.coordinator_id);
    setSelectedCoordinator(existing || null);
    setCoordinatorDialogOpen(true);
  };

  const handleCoordinatorDialogClose = () => {
    setCoordinatorDialogOpen(false);
    setCoordinatorRow(null);
    setSelectedCoordinator(null);
  };

  const handleAssignCoordinatorSave = async () => {
    if (!coordinatorRow || !selectedCoordinator?.id) {
      Swal.fire({ icon: "warning", title: "Select a coordinator" });
      return;
    }
    setSavingCoordinator(true);
    try {
      const res = await patchRequest(
        `${GET_URL.company.api}${coordinatorRow.id}/`,
        { coordinator: selectedCoordinator.id },
        props
      );
      if (res?.status === 200) {
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Coordinator assigned",
          showConfirmButton: false,
          timer: 1200,
        });
        handleCoordinatorDialogClose();
        getCompanyList(page, pageSize);
      }
    } catch {
      Swal.fire({ icon: "error", title: "Unable to assign coordinator" });
    } finally {
      setSavingCoordinator(false);
    }
  };

  const handleActivate = (id) => {
    patchRequest(`${GET_URL.company.api}${id}/`, { is_active: true }, props).then((response) => {
      if (response && response.status === 200) {
        Swal.fire({
          position: "top-end",
          icon: "success",
          title: "Company activated",
          showConfirmButton: false,
          timer: 1200,
        });
        setStatusFilter("active");
        setPage(0);
      }
    }).catch(() => Swal.fire({ icon: "error", title: "Unable to activate company" }));
  };

  const [summaryPage, setSummaryPage] = React.useState(0);
  const [summaryPageSize, setSummaryPageSize] = React.useState(5);

  const companyOptions = React.useMemo(() => {
    const unique = [];
    const seen = new Set();
    (summaryData.company_wise || []).forEach(c => {
      if (c.company_id && !seen.has(c.company_id)) {
        seen.add(c.company_id);
        unique.push({ id: c.company_id, name: c.company_name });
      }
    });
    return [{ id: 'all', name: 'All Companies' }, ...unique];
  }, [summaryData.company_wise]);

  const filteredSummaryRows = React.useMemo(() => {
    const rows = (summaryData.company_wise || []).map((row, idx) => ({
      ...row,
      id: row.company_id || row.company_name || idx,
      slNo: idx + 1
    }));
    if (selectedSummaryCompany && selectedSummaryCompany.id !== 'all') {
      return rows.filter(row => String(row.company_id) === String(selectedSummaryCompany.id));
    }
    return rows;
  }, [summaryData.company_wise, selectedSummaryCompany]);

  const summaryColumns = React.useMemo(() => [
    {
      field: "slNo",
      headerName: "Sl No",
      width: 80,
    },
    {
      field: "company_name",
      headerName: "Company Name",
      flex: 1,
      minWidth: 200,
      valueGetter: (p) => p.row?.company_name || "-",
    },
    {
      field: "coordinator_name",
      headerName: "Coordinator",
      flex: 1,
      minWidth: 150,
      valueGetter: (p) => p.row?.coordinator_name || "-",
    },
    {
      field: "open",
      headerName: "Open Tickets",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const count = params.row?.open || 0;
        return (
          <Chip
            label={count}
            size="small"
            sx={{
              fontWeight: 700,
              backgroundColor: count > 0 ? "#eff6ff" : "#f2f4f7",
              color: count > 0 ? "#1d4ed8" : "#475467",
              border: "1px solid",
              borderColor: count > 0 ? "#bfdbfe" : "#eaecf0",
            }}
          />
        );
      }
    },
    {
      field: "closed",
      headerName: "Closed Tickets",
      width: 150,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const count = params.row?.closed || 0;
        return (
          <Chip
            label={count}
            size="small"
            sx={{
              fontWeight: 700,
              backgroundColor: count > 0 ? "#f0fdf4" : "#f2f4f7",
              color: count > 0 ? "#15803d" : "#475467",
              border: "1px solid",
              borderColor: count > 0 ? "#bbf7d0" : "#eaecf0",
            }}
          />
        );
      }
    }
  ], []);

  const activeColumns = [
    {
      field: "name",
      headerName: "Company Name",
      width: 200,
      minWidth: 200,
      flex: 0,
      headerClassName: "sticky-company-col",
      cellClassName: "sticky-company-col",
      renderCell: renderCompanyNameCell,
    },
    {
      field: "domain",
      headerName: "Domain (URL)",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const val = params.row?.domain;
        if (!val) return "-";
        const url = `https://${val}.edubricz.com`;
        return (
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1976d2' }} onClick={(e) => e.stopPropagation()}>
            {val}.edubricz.com
          </a>
        );
      }
    },
    {
      field: "institute_type",
      headerName: "Institute Type",
      flex: 1,
      minWidth: 140,
      valueGetter: (p) => {
        const labels = { school: "School", nursing: "Nursing", college: "College", engineering: "Engineering" };
        const raw = p.row?.institute_type;
        const values = Array.isArray(raw)
          ? raw
          : String(raw || "school").split(",").map((item) => item.trim()).filter(Boolean);
        return values.map((item) => labels[item] || item).join(", ") || "School";
      },
    },
    {
      field: "status_by_team",
      headerName: "Status (Team)",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const val = params.row?.status_by_team || "Active";
        if (!canUpdate) {
          return (
            <Chip
              label={val}
              size="small"
              variant="outlined"
              sx={{
                fontWeight: 700,
                ...statusStyle(val),
              }}
            />
          );
        }
        const s = statusStyle(val);
        return (
          <FormControl size="small" sx={{ width: 150 }} onClick={(e) => e.stopPropagation()}>
            <Select
              value={val}
              sx={{
                bgcolor: s.bgcolor,
                color: s.color,
                fontWeight: 700,
                borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: s.borderColor },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: s.borderColor },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: s.borderColor },
                "& .MuiSelect-icon": { color: s.color },
              }}
              onChange={(e) => {
                const next = e.target.value;
                const id = params.id;
                // optimistic update
                setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status_by_team: next } : r)));
                patchRequest(`${GET_URL.company.api}${id}/`, { status_by_team: next }, props).catch(() => {
                  // rollback if failed
                  setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status_by_team: val } : r)));
                  Swal.fire({ icon: "error", title: "Unable to update team status" });
                });
              }}
            >
              {statusOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: "status_by_system",
      headerName: "Status (System)",
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        const val = params.row?.status_by_system || "Active";
        return (
          <Chip
            label={val}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: 700,
              ...statusStyle(val),
            }}
          />
        );
      },
    },
    {
      field: "payment_gateway_enabled",
      headerName: "Payment Gateway",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Enabled" : "Disabled"}
          color={params.value ? "success" : "default"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "coordinator_name",
      headerName: "Coordinator",
      flex: 1,
      minWidth: 140,
      valueGetter: (p) => p.row?.coordinator_name || "-",
    },
    {
      field: "created",
      headerName: "Created At",
      flex: 1,
      minWidth: 130,
      valueGetter: (p) => formatCreatedAt(p.row?.created),
    },

    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      minWidth: 460,
      flex: 1.3,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button size="small" variant="outlined" onClick={() => openAssignCoordinator(params.row)}>
            Assign Coordinator
          </Button>
          <Button size="small" variant="text" color="primary" onClick={() => handleDetails(params.id)}>
            Details
          </Button>
          {canUpdate && (
            <Button size="small" variant="outlined" onClick={() => handleEdit(params.id)}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={!params.row?.is_active}
              onClick={() => handleDelete(params.id)}
            >
              Delete
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  const inactiveColumns = [
    {
      field: "name",
      headerName: "Company Name",
      width: 200,
      minWidth: 200,
      flex: 0,
      headerClassName: "sticky-company-col",
      cellClassName: "sticky-company-col",
      renderCell: renderCompanyNameCell,
    },
    {
      field: "domain",
      headerName: "Domain (URL)",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => {
        const val = params.row?.domain;
        if (!val) return "-";
        const url = `https://${val}.edubricz.com`;
        return (
          <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1976d2' }} onClick={(e) => e.stopPropagation()}>
            {val}.edubricz.com
          </a>
        );
      }
    },
    {
      field: "institute_type",
      headerName: "Institute Type",
      flex: 1,
      minWidth: 140,
      valueGetter: (p) => {
        const labels = { school: "School", nursing: "Nursing", college: "College", engineering: "Engineering" };
        const raw = p.row?.institute_type;
        const values = Array.isArray(raw)
          ? raw
          : String(raw || "school").split(",").map((item) => item.trim()).filter(Boolean);
        return values.map((item) => labels[item] || item).join(", ") || "School";
      },
    },
    {
      field: "payment_gateway_enabled",
      headerName: "Payment Gateway",
      flex: 1,
      minWidth: 170,
      renderCell: (params) => (
        <Chip
          label={params.value ? "Enabled" : "Disabled"}
          color={params.value ? "success" : "default"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      minWidth: 160,
      flex: 0.8,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => handleActivate(params.id)}
        >
          Activate
        </Button>
      ),
    },
  ];

  const columns = React.useMemo(
    () => (statusFilter === "inactive" ? inactiveColumns : activeColumns),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusFilter, rows, canUpdate, canDelete]
  );

  const getCompanyList = (nextPage = page, nextPageSize = pageSize) => {
    const url = GET_URL.company.api;
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;
    setLoading(true);
    const params = {
      limit: nextPageSize,
      pageno: nextPage + 1,
    };
    if (nameSearch) {
      params.name = nameSearch;
    }
    if (statusFilter && statusFilter !== "all") {
      params.status = statusFilter;
    }
    if (statusByTeamFilter && statusByTeamFilter !== "all") {
      params.status_by_team = statusByTeamFilter;
    }
    if (featuresFilter.length > 0) {
      params.features = featuresFilter.join(",");
    } else {
      params.features = "none";
    }
    if (coordinatorFilter && coordinatorFilter !== "all") {
      params.coordinator_id = coordinatorFilter;
    }
    if (sortModel && sortModel.length > 0) {
      const sort = sortModel[0];
      const field = sort.field;
      const sortDir = sort.sort === "asc" ? "" : "-";
      params.ordering = `${sortDir}${field}`;
    }
    getRequest(url, params, props)
      .then((response) => {
        if (latestRequestRef.current !== requestId) {
          return;
        }
        if (response && response.status === 200) {
          const data = response.data.data || [];
          setRows(data);
          setRowCount(response.data?.count || data.length);
          getTicketSummary();
        }
      })
      .finally(() => {
        if (latestRequestRef.current === requestId) {
          setLoading(false);
        }
      });
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4} textAlign="left">
          <Typography variant="h5" fontWeight={600}>
            Company Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage all your companies in one place
          </Typography>
        </Grid>

        <Grid item xs={12} md={8} textAlign="right">
          {canUpdate && (
            <Button
              variant="outlined"
              color="info"
              onClick={handleSyncOwnerDetails}
              startIcon={<SyncOutlined />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                px: 1,
                py: 1,
                mr: 3,
                boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
              }}
            >
              Sync Owner Details
            </Button>
          )}
          {canUpdate && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={openOwnerDialog}
              startIcon={<EditOutlined />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                px: 1,
                py: 1,
                mr: 1,
                boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
              }}
            >
              Update Owner Details
            </Button>
          )}
          {isSuperuser && (
            <Button variant="outlined" color="primary" onClick={async(event) => {
              const btn = event.currentTarget;
              const originalText = btn.innerText;
              btn.innerText = "Fetching all data...";
              btn.disabled = true; 
              try{
                let allRows = [];
                let currentPage = 1;
                let hasMore = true;
                while (hasMore) {
                  const response = await getRequest(GET_URL.company.api, { page: 1, limit: 1000 }, props);
                  const results = response?.data?.data || [];
                  if (!results || results.length === 0){
                    hasMore = false;
                  }else {
                    allRows = [...allRows, ...results];
                    if (results.length < 1000){
                      hasMore = false;
                    } else {
                      currentPage++;
                    }
                  }
                }
                if (allRows.length === 0){
                  alert("No data available to download!");
                  btn.disabled = false;
                  btn.innerText = originalText;
                  return;
                }
                const headers = Object.keys(allRows[0]);
                const csvContent = [headers.join(","), ...allRows.map(row => headers.map(header => {
                  const val = row[header] !== null && row[header] !== undefined ? String(row[header]) : "";
                  return `"${val.replace(/"/g, '""')}"`;
                }).join(",")
              )
            ].join("\n");
            const blob = new Blob([csvContent], {type: "text/csv;charset=utf-8;"});
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "all_companies_list.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            }catch (error) {
              alert("Failed: " + (error.message || JSON.stringify(error)));
            }
            btn.disabled = false;
            btn.innerText = originalText;
            }} sx={{ borderRadius: "10px", textTransform: "none", px: 1, py: 1, mr: 2, boxShadow: "0 4px 14px rgba(0,0,0,0.05)", }}>
              Download List       
            </Button>
          )}
          {canCreate && (
            <Button
              variant="contained"
              component={Link}
              to="/company/add"
              startIcon={<AddCircleOutlineOutlined />}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                px: 1,
                py: 1,
                boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
              }}
            >
              Add Company
            </Button>
          )}
        </Grid>
      </Grid>

      {/* Navigation Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{
          mt: 3,
          mb: 3,
          borderBottom: '1px solid #e2e8f0',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '1rem',
            minWidth: 120,
          }
        }}
      >
        <Tab label="Companies" />
        <Tab label="Ticket Summary" />
      </Tabs>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Search by company name / institute code"
            placeholder="Type to filter…"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            variant="outlined"
            size="small"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="company-status-by-team-filter-label">Status (Team)</InputLabel>
            <Select
              labelId="company-status-by-team-filter-label"
              value={statusByTeamFilter}
              label="Status (Team)"
              onChange={(e) => {
                setPage(0);
                setStatusByTeamFilter(e.target.value);
              }}
            >
              {TEAM_STATUS_CHOICES.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="company-features-filter-label">Features</InputLabel>
            <Select
              labelId="company-features-filter-label"
              multiple
              value={featuresFilter}
              label="Features"
              onChange={(e) => {
                setPage(0);
                const val = e.target.value;
                setFeaturesFilter(typeof val === 'string' ? val.split(',') : val);
              }}
              renderValue={(selected) => selected.map(s => s.replace('_', ' ')).join(', ')}
            >
              <MenuItem value="payment_gateway">Payment Gateway</MenuItem>
              <MenuItem value="website">Website</MenuItem>
              <MenuItem value="biometric">Biometric</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="company-coordinator-filter-label">Coordinator</InputLabel>
            <Select
              labelId="company-coordinator-filter-label"
              value={coordinatorFilter}
              label="Coordinator"
              onChange={(e) => {
                setPage(0);
                setCoordinatorFilter(e.target.value);
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {coordinatorUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.username || user.email || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
          sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" } }}
        >
          <Tabs
            value={statusFilter}
            onChange={(_, v) => {
              setPage(0);
              setStatusFilter(v);
            }}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              minHeight: 36,
              "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontWeight: 700 },
            }}
          >
            <Tab value="active" label="Active" />
            <Tab value="inactive" label="Inactive" />
          </Tabs>
        </Grid>
      </Grid>

      {/* Table Card — Companies Tab */}
      {activeTab === 0 && (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 2,
            borderRadius: "16px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
          }}
        >
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pagination
            paginationMode="server"
            sortingMode="server"
            sortModel={sortModel}
            onSortModelChange={(model) => {
              setPage(0);
              setSortModel(model);
            }}
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            rowsPerPageOptions={[10, 20, 50]}
            rowCount={rowCount}
            autoHeight
            disableSelectionOnClick
            onCellDoubleClick={(params, event) => {
              if (params.field !== "name") return;
              event.defaultMuiPrevented = true;
              copyCompanyCode(params.row);
            }}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f5f7fb",
                fontWeight: 600,
                zIndex: 3,
              },
              "& .MuiDataGrid-columnHeadersInner": {
                overflow: "visible",
              },
              "& .MuiDataGrid-row": {
                borderRadius: "8px",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f0f0f0",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#fafafa",
              },
              "& .sticky-company-col": {
                position: "sticky !important",
                left: 0,
                zIndex: 2,
                backgroundColor: "#fff",
                boxShadow: "2px 0 6px -2px rgba(15, 23, 42, 0.12)",
              },
              "& .MuiDataGrid-columnHeaders .sticky-company-col, & .MuiDataGrid-columnHeadersInner .sticky-company-col":
              {
                zIndex: 6,
                backgroundColor: "#f5f7fb !important",
              },
              "& .MuiDataGrid-row:hover .sticky-company-col": {
                backgroundColor: "#fafafa !important",
              },
              "& .MuiDataGrid-columnHeaders .MuiDataGrid-row:hover .sticky-company-col": {
                backgroundColor: "#f5f7fb !important",
              },
            }}
          />
        </Paper>
      )}

      {/* Ticket Summary Tab */}
      {activeTab === 1 && (
        <Box sx={{ mt: 3 }}>
          {/* Overall summary cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              {
                label: "Total Tickets",
                value: summaryData.overall?.total ?? 0,
                bg: "#f0f4ff",
                color: "#3730a3",
                border: "#c7d2fe",
              },
              {
                label: "Open Tickets",
                value: summaryData.overall?.open ?? 0,
                bg: "#eff6ff",
                color: "#1d4ed8",
                border: "#bfdbfe",
              },
              {
                label: "Closed Tickets",
                value: summaryData.overall?.closed ?? 0,
                bg: "#f0fdf4",
                color: "#15803d",
                border: "#bbf7d0",
              },
            ].map((card) => (
              <Grid item xs={12} sm={4} key={card.label}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: "14px",
                    border: `1px solid ${card.border}`,
                    backgroundColor: card.bg,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="body2" sx={{ color: card.color, fontWeight: 600 }}>
                    {card.label}
                  </Typography>
                  <Typography variant="h4" sx={{ color: card.color, fontWeight: 800 }}>
                    {card.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Company-wise breakdown */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: "16px",
              boxShadow: "0 6px 20px rgba(0,0,0,0.05)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Company-wise Ticket Breakdown
              </Typography>
              <Autocomplete
                size="small"
                options={companyOptions}
                getOptionLabel={(o) => o.name || ""}
                value={selectedSummaryCompany || companyOptions[0] || null}
                onChange={(_, val) => setSelectedSummaryCompany(val)}
                isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                sx={{ width: 260 }}
                renderInput={(params) => (
                  <TextField {...params} label="Filter by company" placeholder="All Companies" />
                )}
              />
            </Box>
            <DataGrid
              rows={filteredSummaryRows}
              columns={summaryColumns}
              pageSize={summaryPageSize}
              onPageSizeChange={(s) => { setSummaryPageSize(s); }}
              rowsPerPageOptions={[5, 10, 20]}
              autoHeight
              disableSelectionOnClick
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f5f7fb",
                  fontWeight: 700,
                },
                "& .MuiDataGrid-cell": { borderBottom: "1px solid #f0f0f0" },
                "& .MuiDataGrid-row:hover": { backgroundColor: "#fafafa" },
              }}
            />
          </Paper>
        </Box>
      )}

      <Dialog open={coordinatorDialogOpen} onClose={handleCoordinatorDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle>Assign coordinator</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {coordinatorRow?.name || "Company"}
          </Typography>
          <Autocomplete
            options={coordinatorUsers}
            getOptionLabel={(opt) =>
              opt?.username || opt?.email || `${opt?.first_name || ""} ${opt?.last_name || ""}`.trim() || ""
            }
            value={selectedCoordinator}
            onChange={(_, val) => setSelectedCoordinator(val)}
            renderInput={(params) => (
              <TextField {...params} label="Coordinator" size="small" placeholder="Select user" />
            )}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCoordinatorDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignCoordinatorSave}
            disabled={savingCoordinator || !selectedCoordinator}
            startIcon={savingCoordinator ? <CircularProgress size={18} color="inherit" /> : null}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ownerDialogOpen} onClose={handleOwnerDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>Update Owner Details</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            label="Search by institute name / code"
            placeholder="Type to filter…"
            value={ownerSearchInput}
            onChange={(e) => setOwnerSearchInput(e.target.value)}
            sx={{ mt: 1, mb: 1 }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={ownerNameNotFilled}
                  onChange={(e) => {
                    setOwnerNameNotFilled(e.target.checked);
                    setOwnerPage(0);
                  }}
                />
              }
              label="Name not filled"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={ownerEmailNotFilled}
                  onChange={(e) => {
                    setOwnerEmailNotFilled(e.target.checked);
                    setOwnerPage(0);
                  }}
                />
              }
              label="Email not filled"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={ownerNumberNotFilled}
                  onChange={(e) => {
                    setOwnerNumberNotFilled(e.target.checked);
                    setOwnerPage(0);
                  }}
                />
              }
              label="Number not filled"
            />
          </Stack>
          <Box sx={{ width: "100%", height: 480 }}>
            <DataGrid
              rows={ownerRows}
              columns={ownerColumns}
              loading={ownerLoading}
              pagination
              paginationMode="server"
              page={ownerPage}
              pageSize={ownerPageSize}
              onPageChange={(newPage) => setOwnerPage(newPage)}
              onPageSizeChange={(newSize) => {
                setOwnerPageSize(newSize);
                setOwnerPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              rowCount={ownerRowCount}
              disableSelectionOnClick
              getRowHeight={() => 72}
              sx={{
                border: "none",
                overflow: "visible",
                "& .MuiDataGrid-main": { overflow: "visible" },
                "& .MuiDataGrid-virtualScroller": { overflow: "auto !important" },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f5f7fb",
                  fontWeight: 600,
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f0f0f0",
                  alignItems: "center",
                  display: "flex",
                  overflow: "visible !important",
                },
                "& .MuiDataGrid-row": {
                  overflow: "visible",
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOwnerDialogClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CompanyList;